import { useEffect } from "react"

interface PageSEOOptions {
  title: string
  description: string
  path: string
  type?: string
  /** Absolute or root-relative image for social cards. Defaults to the app icon. */
  image?: string
  /** Structured data. One object or an array; @context is added automatically. */
  jsonLd?: Record<string, any> | Record<string, any>[]
  /** Keep the page out of search results (auth screens, thin pages). */
  noindex?: boolean
}

const BASE_URL = "https://rent-keep.com"
const DEFAULT_IMAGE = `${BASE_URL}/app-icon.png`
const JSONLD_ID = "page-jsonld"

function setMeta(key: string, content: string, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute("content", content)
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement("link")
    link.setAttribute("rel", "canonical")
    document.head.appendChild(link)
  }
  link.setAttribute("href", href)
}

export function usePageSEO({
  title,
  description,
  path,
  type = "website",
  image,
  jsonLd,
  noindex = false,
}: PageSEOOptions) {
  // jsonLd is typically an inline object literal, so it is a new reference on
  // every render. Serialising it keeps this effect from re-running forever.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : ""

  useEffect(() => {
    const prev = document.title
    const url = `${BASE_URL}${path}`
    const img = image
      ? image.startsWith("http")
        ? image
        : `${BASE_URL}${image}`
      : DEFAULT_IMAGE

    document.title = title
    setMeta("description", description)

    setMeta("og:title", title, "property")
    setMeta("og:description", description, "property")
    setMeta("og:type", type, "property")
    setMeta("og:url", url, "property")
    setMeta("og:image", img, "property")
    setMeta("og:site_name", "RentKeep", "property")

    setMeta("twitter:card", "summary_large_image")
    setMeta("twitter:title", title)
    setMeta("twitter:description", description)
    setMeta("twitter:image", img)

    setMeta(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    )

    setCanonical(url)

    // Structured data. Tagged with an id so client-side navigation replaces the
    // previous page's block instead of stacking duplicates.
    document.getElementById(JSONLD_ID)?.remove()
    if (jsonLdKey) {
      const parsed = JSON.parse(jsonLdKey)
      const withContext = Array.isArray(parsed)
        ? parsed.map((item) => ({ "@context": "https://schema.org", ...item }))
        : { "@context": "https://schema.org", ...parsed }

      const script = document.createElement("script")
      script.type = "application/ld+json"
      script.id = JSONLD_ID
      script.textContent = JSON.stringify(withContext)
      document.head.appendChild(script)
    }

    return () => {
      document.title = prev
      document.getElementById(JSONLD_ID)?.remove()
    }
  }, [title, description, path, type, image, jsonLdKey, noindex])
}
